import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { AcessoPerfil } from "../../../../interfaces/acessos";
import { convidarPessoa } from "../../../../services/acessosApi";
import Modal from "../../../../components/modal";
import { FalhaEnvio, enviarEmSequencia, nomeDoEmail, separarEmails } from "../../../../utils/separarEmails";
import { BTN_PRIMARIO, BTN_SECUNDARIO, INPUT, LABEL, msgErro } from "../../paineis-admin/components/shared";

interface Props {
  orgId: number;
  perfis: AcessoPerfil[];
  onClose: () => void;
  onConvidado: () => void;
}

const CAIXA_ERRO =
  "rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300";

/** Convite por e-mail (um ou vários): o backend cria o acesso e envia o link para a pessoa criar a senha. */
const ConvidarModal: React.FC<Props> = ({ orgId, perfis, onClose, onConvidado }) => {
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [perfilId, setPerfilId] = useState<string>(perfis.length === 1 ? String(perfis[0].id) : "");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [falhas, setFalhas] = useState<FalhaEnvio[]>([]);

  const { validos, invalidos } = useMemo(() => separarEmails(email), [email]);
  const varios = validos.length > 1;
  const valido = validos.length > 0 && invalidos.length === 0 && (varios || nome.trim() !== "") && perfilId !== "";

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valido || enviando) return;
    setEnviando(true);
    setErro(null);
    setFalhas([]);
    const { enviados, falhas: falhou } = await enviarEmSequencia(
      validos,
      (alvo) =>
        convidarPessoa(orgId, {
          email: alvo,
          nome: varios ? nomeDoEmail(alvo) : nome.trim(),
          perfilId: Number(perfilId),
        }),
      // Inclui o 409 "Esta pessoa já tem acesso a esta organização." — o texto vem do backend.
      (err) => msgErro(err, "Não foi possível enviar o convite. Tente de novo.")
    );
    setEnviando(false);
    if (enviados.length > 0) onConvidado();
    if (falhou.length === 0) {
      toast.success(
        enviados.length === 1 ? `Convite enviado para ${enviados[0]}` : `${enviados.length} convites enviados`
      );
      onClose();
      return;
    }
    if (validos.length === 1) {
      setErro(falhou[0].mensagem);
      return;
    }
    setFalhas(falhou);
    setEmail(falhou.map((f) => f.email).join(", "));
    if (enviados.length > 0) toast.success(`${enviados.length} convite(s) enviado(s)`);
  };

  return (
    <Modal onClose={onClose}>
      <form
        onSubmit={enviar}
        onClick={(e) => e.stopPropagation()}
        aria-labelledby="convidar-titulo"
        className="w-full max-w-md space-y-4 rounded-[20px] bg-white p-6 shadow-dv-md dark:bg-navy-800"
      >
        <h2 id="convidar-titulo" className="font-display text-lg font-extrabold text-navy-700 dark:text-white">
          Convidar pessoa
        </h2>

        <div>
          <label htmlFor="convite-email" className={LABEL}>
            E-mail
          </label>
          <textarea
            id="convite-email"
            rows={2}
            className={`${INPUT} mt-1 resize-y`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby="convite-email-ajuda"
            autoFocus
          />
          <p id="convite-email-ajuda" className="mt-1 text-xs text-gray-600 dark:text-gray-300">
            Separe vários por vírgula ou cole um por linha.
          </p>
          {invalidos.length > 0 && (
            <p className="mt-1 text-xs text-red-700 dark:text-red-300">E-mail inválido: {invalidos.join(", ")}</p>
          )}
        </div>
        <div>
          <label htmlFor="convite-nome" className={LABEL}>
            Nome
          </label>
          <input
            id="convite-nome"
            type="text"
            className={`${INPUT} mt-1`}
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            disabled={varios}
          />
          {varios && (
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
              Com vários e-mails, o nome de cada pessoa vem do próprio e-mail; ajuste depois em Pessoas.
            </p>
          )}
        </div>
        <div>
          <label htmlFor="convite-perfil" className={LABEL}>
            Perfil
          </label>
          <select
            id="convite-perfil"
            className={`${INPUT} mt-1`}
            value={perfilId}
            onChange={(e) => setPerfilId(e.target.value)}
          >
            <option value="">Escolha um perfil</option>
            {perfis.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </select>
        </div>

        {falhas.length > 0 && (
          <div role="alert" className={CAIXA_ERRO}>
            <p className="font-semibold">Não foi possível convidar:</p>
            <ul className="mt-1 list-disc pl-5">
              {falhas.map((f) => (
                <li key={f.email}>
                  {f.email}: {f.mensagem}
                </li>
              ))}
            </ul>
          </div>
        )}
        {erro && (
          <p role="alert" className={CAIXA_ERRO}>
            {erro}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className={BTN_SECUNDARIO} onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className={BTN_PRIMARIO} disabled={!valido || enviando}>
            {enviando ? "Enviando..." : "Enviar convite"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ConvidarModal;
